import * as React from 'react';
import {Link} from 'react-router-dom';

import classnames from 'classnames';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {LinkWrapper} from 'shells/navigation/secondary-sidebar/link-wrapper';

import type {NavItemObj} from '../types';

import {Count} from './count';
import {TwoLineNavItemContent} from './two-line-nav-item-content';

import styles from './nav-item.module.css';

export type Props = {
    navItem: NavItemObj;
    onEdit: () => void;
    onClick: () => void;
    onDelete?: () => void;
    onShare?: (navItem: NavItemObj) => void | null | undefined;
    isTwoLine: boolean;
};

export function NavItem(props: Props) {
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = React.useState(false);
    const [isActionMenuOpen, setIsActionMenuOpen] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    let options = null;
    if (props.navItem.id) {
        options = [
            {
                label: props.navItem.isImmutable ? 'Make a copy' : 'Edit',
                variant: 'secondary',
                handleSelect: props.onEdit,
            },
        ];

        if (props.onShare) {
            options.push({
                handleSelect: () => props.onShare && props.onShare(props.navItem),
                label: 'Share',
                variant: 'secondary',
            });
        }

        if (props.onDelete) {
            options.push({
                label: props.navItem.type === 'list' ? 'Delete' : 'Archive',
                variant: 'destructive',
                handleSelect: () => {
                    setIsDeleteConfirmationOpen(true);
                },
            });
        }
    }

    return (
        <>
            <LinkWrapper isActive={props.navItem.isActive} isHovered={isHovered}>
                <LinkComponent
                    className={classnames(styles.link, {
                        [styles['link--two-line']]: props.isTwoLine,
                    })}
                    link={props.navItem.link || '#'}
                    onClick={props.onClick}
                    onMouseEnter={() => {
                        setIsHovered(true);
                    }}
                    onMouseLeave={() => {
                        setIsHovered(false);
                    }}
                    isDisabled={Boolean(props.navItem.isActive || props.navItem.isUnsavedList)}
                >
                    {props.isTwoLine && NutClientConfig.hasSharedListsFF ? (
                        <TwoLineNavItemContent navItem={props.navItem} />
                    ) : (
                        <>
                            <span data-tip={props.navItem.name} className={styles['nav-item-name']}>
                                {props.navItem.name}
                            </span>
                            <span className={styles['nav-item-count']}>
                                <Count navItem={props.navItem} />
                            </span>
                        </>
                    )}
                    {(isHovered || isActionMenuOpen || props.navItem.isActive) && options && (
                        <div className={styles['action-menu-container']}>
                            <ActionMenu
                                size='tiny'
                                padding='none'
                                dotColor={props.navItem.isActive ? 'grey' : 'off-white'}
                                // @ts-expect-error
                                options={options}
                                onOpenChange={(isOpen) => {
                                    setIsActionMenuOpen(isOpen);
                                }}
                                offsetX={16}
                                offsetY={9}
                            />
                        </div>
                    )}
                </LinkComponent>
            </LinkWrapper>
            {isDeleteConfirmationOpen && (
                <ConfirmationDialog
                    title={
                        props.navItem.type === 'list' ? 'Delete this list' : 'Delete this audience'
                    }
                    message={
                        props.navItem.name
                            ? `Are you sure you want to delete '${props.navItem.name}'?`
                            : 'Are you sure you want to delete this list?'
                    }
                    confirmText='Delete'
                    cancelText='Cancel'
                    onCancel={() => {
                        setIsDeleteConfirmationOpen(false);
                    }}
                    onConfirm={props.onDelete}
                    isConfirmDestructive={true}
                    // @ts-expect-error
                    errorMessage={
                        props.navItem.id ? null : 'Something went wrong. Please try again later.'
                    }
                />
            )}
        </>
    );
}

type LinkComponentProps = {
    isDisabled: boolean;
    onClick: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    className: string;
    children: React.ReactNode;
    link: string;
};

// We don't want to actually disable the link, because it prevents hover interactions
// that we require to show action menus, truncated titles, etc.
// Returning an 'a' tag with an undefined href prevents the default action of the link
function LinkComponent(props: LinkComponentProps) {
    const {isDisabled, link, children, ...restProps} = props;

    if (isDisabled) {
        return (
            <a href={undefined} {...restProps}>
                {children}
            </a>
        );
    }

    return (
        // @ts-expect-error
        <Link to={link} {...restProps}>
            {children}
        </Link>
    );
}
